import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  ActivityIndicator,
  ViewStyle,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import {
  useStripe,
  CardField,
  PaymentIntent,
} from '@stripe/stripe-react-native';
import { usePaymentsContext } from '../providers/PaymentsProvider';
import { ERROR_MESSAGES } from '../../shared/constants';
import { validateEmail } from '../../shared/utils/validation';

interface PaymentFormProps {
  clientSecret?: string;
  customerId?: string;
  customerEmail?: string;
  amount?: number;
  currency?: string;
  description?: string;
  metadata?: Record<string, string>;
  onSuccess?: (paymentIntent: PaymentIntent.Result) => void;
  onError?: (error: string) => void;
  onLoading?: (loading: boolean) => void;
  containerStyle?: ViewStyle;
  showBillingDetails?: boolean;
}

export const PaymentForm: React.FC<PaymentFormProps> = ({
  clientSecret,
  customerId,
  customerEmail,
  amount,
  currency = 'usd',
  description,
  metadata,
  onSuccess,
  onError,
  onLoading,
  containerStyle,
  showBillingDetails = true,
}) => {
  const { confirmPayment } = useStripe();
  const { initialized } = usePaymentsContext();

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(customerEmail || '');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [cardComplete, setCardComplete] = useState(false);

  const handleError = (errorMessage: string) => {
    onError?.(errorMessage);
    Alert.alert('Payment Error', errorMessage);
    console.error('Payment error:', errorMessage);
  };

  const handlePayment = async () => {
    if (!initialized) {
      handleError(ERROR_MESSAGES.PROVIDER_NOT_CONFIGURED);
      return;
    }

    if (showBillingDetails && email && !validateEmail(email)) {
      handleError('Please enter a valid email address');
      return;
    }

    if (!cardComplete) {
      handleError('Please complete your card information');
      return;
    }

    let paymentClientSecret = clientSecret;

    // If no client secret provided, create payment intent
    if (!paymentClientSecret && amount) {
      try {
        const response = await fetch('/api/payments/create-payment-intent', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount,
            currency,
            customerId,
            description,
            metadata,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to create payment intent');
        }

        const { client_secret } = await response.json();
        paymentClientSecret = client_secret;
      } catch {
        handleError('Failed to initialize payment');
        return;
      }
    }

    if (!paymentClientSecret) {
      handleError('Payment could not be initialized');
      return;
    }

    setLoading(true);
    onLoading?.(true);

    try {
      const billingDetails = showBillingDetails
        ? {
            email: email || undefined,
            name: name || undefined,
            phone: phone || undefined,
          }
        : undefined;

      const { error: stripeError, paymentIntent } = await confirmPayment(
        paymentClientSecret,
        {
          paymentMethodType: 'Card',
          paymentMethodData: {
            billingDetails,
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message || ERROR_MESSAGES.PAYMENT_FAILED);
      }

      if (paymentIntent?.status === 'Succeeded') {
        onSuccess?.(paymentIntent);
        Alert.alert('Success', 'Payment completed successfully!');
      } else {
        throw new Error('Payment was not completed');
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : ERROR_MESSAGES.PAYMENT_FAILED;
      handleError(errorMessage);
    } finally {
      setLoading(false);
      onLoading?.(false);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={[styles.container, containerStyle]}
    >
      <ScrollView showsVerticalScrollIndicator={false}>
        {showBillingDetails && (
          <View style={styles.billingSection}>
            <Text style={styles.sectionTitle}>Billing Information</Text>

            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Email *</Text>
              <TextInput
                style={styles.textInput}
                value={email}
                onChangeText={setEmail}
                placeholder="your@email.com"
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
              />
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Full Name</Text>
              <TextInput
                style={styles.textInput}
                value={name}
                onChangeText={setName}
                placeholder="John Doe"
                autoCapitalize="words"
              />
            </View>

            <View style={styles.inputContainer}>
              <Text style={styles.inputLabel}>Phone</Text>
              <TextInput
                style={styles.textInput}
                value={phone}
                onChangeText={setPhone}
                placeholder="+1 (555) 123-4567"
                keyboardType="phone-pad"
              />
            </View>
          </View>
        )}

        <View style={styles.cardSection}>
          <Text style={styles.sectionTitle}>Payment Information</Text>
          <View style={styles.cardContainer}>
            <CardField
              postalCodeEnabled={true}
              placeholders={{
                number: '4242 4242 4242 4242',
              }}
              cardStyle={cardFieldStyle}
              style={styles.cardFieldContainer}
              onCardChange={cardDetails => {
                setCardComplete(cardDetails.complete);
              }}
            />
          </View>
        </View>

        <TouchableOpacity
          style={[styles.payButton, loading && styles.payButtonDisabled]}
          onPress={handlePayment}
          disabled={loading || !cardComplete}
          activeOpacity={0.8}
        >
          {loading ? (
            <ActivityIndicator color="#ffffff" size="small" />
          ) : (
            <Text style={styles.payButtonText}>
              Pay {amount ? `$${(amount / 100).toFixed(2)}` : ''}
            </Text>
          )}
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const cardFieldStyle = {
  backgroundColor: '#fafafa',
  textColor: '#333',
  fontSize: 16,
  placeholderColor: '#999',
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: '#ffffff',
  },
  billingSection: {
    marginBottom: 24,
  },
  cardSection: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 16,
  },
  inputContainer: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#555',
    marginBottom: 8,
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    backgroundColor: '#fafafa',
  },
  cardContainer: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    backgroundColor: '#fafafa',
  },
  cardFieldContainer: {
    width: '100%',
    height: 50,
  },
  payButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 52,
  },
  payButtonDisabled: {
    opacity: 0.5,
  },
  payButtonText: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: '600',
  },
});

export default PaymentForm;
